/**
 * プラン管理 `/plans`。
 *
 * 一覧はテーブルではなくカードグリッド。プランは十数件しか無く、比較して眺めるものなので
 * 「1 プラン = 1 カード」の方が読みやすい（`{items,total}` を返してよい小規模一覧の 1 つ）。
 *
 * **上限（limits）と機能フラグ（features）はここでは編集できない。**
 * 正は `@mail-sv/shared` の `PLAN_CATALOG` で、API 起動時のシードが DB へ
 * 同期する（`packages/db/src/seed/plans.ts`）。画面から書き換えても次の起動で
 * 巻き戻るため、編集できるように見せる方が有害。同じ理由で「作成」「複製」も
 * 置かない（カタログに無い code のプランは起動時に retired にされる）。
 * ここで変えられるのは表示と価格・公開フラグだけ。
 */
import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Layers, Pencil } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Input,
  PageHeader,
  Skeleton,
  SwitchRow,
  formatBytes,
  formatCount,
} from '@mail-sv/ui';
import { labelOf } from '@mail-sv/shared';
import { api } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { formatJpy } from '../lib/format.js';
import { OPS_PERMISSION } from '../lib/permissions.js';
import { usePermission } from '../stores/auth.js';
import { FormDialog } from '../components/dialogs.js';
import { Field } from '../components/form-fields.js';
import { QueryError, RefetchingDot } from '../components/states.js';
import type { CountedList, OpsPlan } from '../types/api.js';

export function PlansPage(): React.ReactElement {
  const canWrite = usePermission(OPS_PERMISSION.planWrite);
  const [editing, setEditing] = React.useState<OpsPlan | null>(null);

  const query = useQuery({
    queryKey: qk.plans(),
    queryFn: () => api.get<CountedList<OpsPlan>>('/plans'),
  });

  return (
    <>
      <PageHeader
        title="プラン"
        description="提供中のプランと上限・機能フラグ。上限と機能はプランカタログ（コード側）が正で、この画面からは変更できません。"
        actions={<RefetchingDot active={query.isFetching && !query.isLoading} />}
      />

      {query.isError ? <QueryError error={query.error} onRetry={() => void query.refetch()} /> : null}

      {query.isLoading ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-72" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {(query.data?.items ?? []).map((plan) => (
            <PlanCard key={plan.code} plan={plan} canWrite={canWrite} onEdit={() => setEditing(plan)} />
          ))}
        </div>
      )}

      <PlanDialog plan={editing} open={editing !== null} onClose={() => setEditing(null)} />
    </>
  );
}

function PlanCard({
  plan,
  canWrite,
  onEdit,
}: {
  plan: OpsPlan;
  canWrite: boolean;
  onEdit: () => void;
}): React.ReactElement {
  return (
    <div className="flex flex-col rounded-xl border border-border bg-card p-5 shadow-lg shadow-black/10">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="truncate text-lg font-semibold">{plan.name}</h2>
            {plan.retired ? (
              <Badge variant="muted" size="sm">
                廃止
              </Badge>
            ) : plan.isPublic ? (
              <Badge variant="success" size="sm">
                公開中
              </Badge>
            ) : (
              <Badge variant="warning" size="sm">
                非公開
              </Badge>
            )}
          </div>
          <code className="text-xs text-muted-foreground">{plan.code}</code>
        </div>
        <Layers className="size-5 shrink-0 text-muted-foreground" aria-hidden />
      </div>

      {/*
        カタログ価格は税抜が正。総額表示（消費税法 63 条）の税込額は
        **サーバーが計算したもの**（`monthlyPriceIncTax`）を出す。
        画面で 1.1 を掛けると、税率が変わったときに直す場所が散らばる。
      */}
      <p className="mt-3 text-2xl font-semibold tabular">
        {formatJpy(plan.monthlyPrice)}
        <span className="ml-1 text-sm font-normal text-muted-foreground">/ 月（税抜）</span>
      </p>
      <p className="text-xs text-muted-foreground">
        税込 {formatJpy(plan.monthlyPriceIncTax)}
        {plan.yearlyPrice !== null
          ? ` ・ 年額 ${formatJpy(plan.yearlyPrice)}（税込 ${formatJpy(plan.yearlyPriceIncTax ?? 0)}）`
          : ''}
      </p>
      {plan.description ? <p className="mt-1 text-sm text-muted-foreground">{plan.description}</p> : null}

      <dl className="mt-4 space-y-1.5 text-sm">
        <LimitRow label="ドメイン" value={limitText(plan.limits.domainsMax)} />
        <LimitRow label="メールボックス" value={limitText(plan.limits.mailboxesMax)} />
        <LimitRow label="1 箱あたり容量" value={formatBytes(plan.limits.mailboxQuotaBytesDefault)} />
        <LimitRow label="総容量" value={formatBytes(plan.limits.storageBytesTotal)} />
        <LimitRow label="1 日送信通数" value={limitText(plan.limits.sendPerDayMax)} />
        <LimitRow label="添付上限" value={formatBytes(plan.limits.messageSizeBytesMax)} />
      </dl>

      <div className="mt-4 flex flex-wrap gap-1.5">
        {Object.entries(plan.features)
          .filter(([, on]) => on)
          .map(([key]) => (
            <Badge key={key} variant="secondary" size="sm">
              {labelOf(key)}
            </Badge>
          ))}
      </div>

      <div className="mt-auto flex items-center justify-between gap-2 pt-4">
        <span className="text-xs text-muted-foreground">契約中 {formatCount(plan.subscriberCount)} 件</span>
        {canWrite ? (
          <Button variant="ghost" size="xs" onClick={onEdit}>
            <Pencil aria-hidden />
            編集
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function LimitRow({ label, value }: { label: string; value: string }): React.ReactElement {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="tabular font-medium">{value}</dd>
    </div>
  );
}

/** 0 / 負値は「無制限」の意味に使う（プラン定義側の約束）。 */
export function limitText(n: number): string {
  return n <= 0 ? '無制限' : formatCount(n);
}

/* ---------------------------------------------------------------- 編集 */

interface PlanForm {
  name: string;
  description: string;
  monthlyPrice: string;
  yearlyPrice: string;
  isPublic: boolean;
  retired: boolean;
}

function toForm(plan: OpsPlan): PlanForm {
  return {
    name: plan.name,
    description: plan.description ?? '',
    monthlyPrice: String(plan.monthlyPrice),
    yearlyPrice: plan.yearlyPrice === null ? '' : String(plan.yearlyPrice),
    isPublic: plan.isPublic,
    retired: plan.retired,
  };
}

function PlanDialog({
  plan,
  open,
  onClose,
}: {
  plan: OpsPlan | null;
  open: boolean;
  onClose: () => void;
}): React.ReactElement | null {
  const qc = useQueryClient();
  const [form, setForm] = React.useState<PlanForm>(() => (plan ? toForm(plan) : EMPTY_FORM));

  React.useEffect(() => {
    if (open && plan) setForm(toForm(plan));
  }, [open, plan]);

  const mut = useMutation({
    mutationFn: () =>
      api.patch(`/plans/${plan?.code ?? ''}`, {
        name: form.name.trim(),
        description: form.description,
        monthlyPrice: Number(form.monthlyPrice),
        // 空欄は「年額なし」。0 と区別するため undefined で送らない。
        ...(form.yearlyPrice.trim().length > 0 ? { yearlyPrice: Number(form.yearlyPrice) } : {}),
        isPublic: form.isPublic,
        retired: form.retired,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.plans() });
      onClose();
    },
  });

  if (!open || !plan) return null;

  return (
    <FormDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={`${plan.name} を編集`}
      submitLabel="保存する"
      pending={mut.isPending}
      error={mut.isError ? mut.error : undefined}
      disabled={form.name.trim().length === 0}
      onSubmit={() => mut.mutate()}
    >
      <Alert variant="info">
        <AlertTitle>上限と機能はここでは変更できません</AlertTitle>
        <AlertDescription>
          <code className="font-mono">{plan.code}</code> の上限・機能フラグはプランカタログ（コード側の定義）が正で、
          API の起動ごとに同期されます。変更するにはカタログを更新してデプロイしてください。
          このプランは現在 {formatCount(plan.subscriberCount)} 件の契約で使われています。
        </AlertDescription>
      </Alert>

      <Field label="プランコード" hint="契約と紐づく識別子。変更できません。">
        <Input value={plan.code} disabled className="font-mono" />
      </Field>
      <Field label="プラン名" required>
        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
      </Field>
      <Field label="説明">
        <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
      </Field>
      <Field label="月額（円）" required>
        <Input
          type="number"
          min={0}
          value={form.monthlyPrice}
          onChange={(e) => setForm({ ...form, monthlyPrice: e.target.value })}
        />
      </Field>
      <Field label="年額（円）" hint="空欄なら年額契約を提供しません。">
        <Input
          type="number"
          min={0}
          value={form.yearlyPrice}
          onChange={(e) => setForm({ ...form, yearlyPrice: e.target.value })}
        />
      </Field>
      <SwitchRow
        label="公開する"
        description="非公開のプランは新規契約で選べません（既存契約はそのまま）。"
        checked={form.isPublic}
        onCheckedChange={(v) => setForm({ ...form, isPublic: v })}
      />
      <SwitchRow
        label="廃止にする"
        description="廃止すると新規契約から外れます。既存契約は継続します。"
        checked={form.retired}
        onCheckedChange={(v) => setForm({ ...form, retired: v })}
      />
    </FormDialog>
  );
}

const EMPTY_FORM: PlanForm = {
  name: '',
  description: '',
  monthlyPrice: '0',
  yearlyPrice: '',
  isPublic: false,
  retired: false,
};
